import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { useT } from "../context/LanguageContext";
import { Layers, MapPin, BookOpen, Plus, Trash2, GraduationCap } from "lucide-react";

export function CurriculumBuilder() {
  const t = useT();
  const [weeks, setWeeks] = useState([]);
  const [form, setForm] = useState({ week_number: "", title: "", focus_points: [""], notes: "" });

  const load = () => apiClient.get("/curriculum").then((r) => setWeeks(r.data.weeks));
  useEffect(() => { load(); }, []);

  const add = async () => {
    const payload = { week_number: Number(form.week_number) || (weeks.length + 1), title: form.title, focus_points: form.focus_points.filter(Boolean), notes: form.notes };
    if (!payload.title) return toast.error(t("Title required"));
    await apiClient.post("/curriculum", payload);
    setForm({ week_number: "", title: "", focus_points: [""], notes: "" });
    load(); toast.success(t("Week added"));
  };
  const del = async (id) => { await apiClient.delete(`/curriculum/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="curriculum-builder">
      <div className="bento-card p-5">
        <h3 className="font-display font-bold text-lg mb-2">{t("Add a week")}</h3>
        <div className="grid md:grid-cols-4 gap-3">
          <div><Label>{t("Week #")}</Label><Input type="number" value={form.week_number} onChange={(e) => setForm({ ...form, week_number: e.target.value })} data-testid="cur-week-num" /></div>
          <div className="md:col-span-3"><Label>{t("Title *")}</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cur-title" /></div>
        </div>
        <div className="mt-3">
          <Label>{t("Focus points")}</Label>
          {form.focus_points.map((fp, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <Input value={fp} onChange={(e) => {
                const cp = [...form.focus_points]; cp[i] = e.target.value; setForm({ ...form, focus_points: cp });
              }} />
              <Button type="button" variant="ghost" onClick={() => setForm({ ...form, focus_points: form.focus_points.filter((_, j) => j !== i) })}>✕</Button>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={() => setForm({ ...form, focus_points: [...form.focus_points, ""] })}><Plus className="w-4 h-4" /> {t("Add focus point")}</Button>
        </div>
        <div className="mt-3"><Label>{t("Notes")}</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        <Button onClick={add} className="btn-pill btn-primary mt-4 h-10" data-testid="save-week">{t("Save week")}</Button>
      </div>

      <div className="space-y-3">
        {weeks.map((w) => (
          <div key={w.id} className="bento-card p-5" data-testid={`week-${w.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider font-bold" style={{ color: "var(--app-text-muted)" }}>{t("Week")} {w.week_number}</div>
                <div className="font-display text-xl font-bold">{w.title}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(w.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
            {w.focus_points?.length > 0 && (
              <ul className="mt-3 list-disc list-inside space-y-0.5 text-sm">
                {w.focus_points.map((fp, i) => <li key={i}>{fp}</li>)}
              </ul>
            )}
            {w.notes && <p className="mt-2 text-sm" style={{ color: "var(--app-text-muted)" }}>{w.notes}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingTable() {
  const t = useT();
  const [cities, setCities] = useState([]);
  const [view, setView] = useState("studio");
  useEffect(() => { apiClient.get("/pricing/cities").then((r) => setCities(r.data.cities)); }, []);
  return (
    <div className="space-y-4" data-testid="pricing-table">
      <div className="flex gap-2">
        <Button variant={view === "studio" ? "default" : "outline"} onClick={() => setView("studio")} data-testid="price-studio-toggle">{t("Studio prices")}</Button>
        <Button variant={view === "driving" ? "default" : "outline"} onClick={() => setView("driving")} data-testid="price-driving-toggle">{t("Driving to student")}</Button>
      </div>
      <div className="bento-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: "var(--app-surface-2)" }}>
              <tr>
                <th className="p-3 text-left">{t("City")}</th><th className="p-3 text-left">{t("State")}</th>
                <th className="p-3 text-right">{view === "studio" ? t("Studio 30-min") : t("At-Student 30-min")}</th>
                <th className="p-3 text-right">{view === "studio" ? t("Studio 60-min") : t("At-Student 60-min")}</th>
              </tr>
            </thead>
            <tbody>
              {cities.map((c) => (
                <tr key={c.id} className="border-t" style={{ borderColor: "var(--app-border)" }} data-testid={`city-row-${c.city}`}>
                  <td className="p-3 font-semibold">{c.city}</td>
                  <td className="p-3">{c.state}</td>
                  <td className="p-3 text-right">${view === "studio" ? c.studio_30min : c.at_student_30min}</td>
                  <td className="p-3 text-right">${view === "studio" ? c.studio_60min : c.at_student_60min}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CustomFields() {
  const t = useT();
  const [fields, setFields] = useState([]);
  useEffect(() => { apiClient.get("/me/custom-fields").then((r) => {
    const arr = r.data.fields || [];
    while (arr.length < 4) arr.push({ name: "", value: "" });
    setFields(arr);
  }); }, []);
  const save = async () => { await apiClient.put("/me/custom-fields", { fields }); toast.success(t("Saved")); };
  return (
    <div className="space-y-3" data-testid="custom-fields">
      <p className="text-sm" style={{ color: "var(--app-text-muted)" }}>{t("Four customizable fields for your own notes, goals, or reminders.")}</p>
      {fields.map((f, i) => (
        <div key={i} className="grid md:grid-cols-2 gap-3 bento-card p-4">
          <div><Label>{t("Field")} {i + 1} {t("label")}</Label><Input value={f.name} onChange={(e) => { const cp = [...fields]; cp[i].name = e.target.value; setFields(cp); }} data-testid={`cf-name-${i}`} /></div>
          <div><Label>{t("Value")}</Label><Textarea value={f.value} onChange={(e) => { const cp = [...fields]; cp[i].value = e.target.value; setFields(cp); }} rows={2} data-testid={`cf-value-${i}`} /></div>
        </div>
      ))}
      <Button onClick={save} className="btn-pill btn-primary h-10" data-testid="save-custom-fields">{t("Save custom fields")}</Button>
    </div>
  );
}

function TeacherListingEditor() {
  const t = useT();
  const [listing, setListing] = useState(null);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    apiClient.get("/me/teacher-listing").then((r) => setListing(r.data.listing || {})).catch(() => setListing({}));
    apiClient.get("/pricing/cities").then((r) => setCities(r.data.cities || [])).catch(() => {});
  }, []);

  if (!listing) return <div className="p-6">{t("Loading…")}</div>;

  const save = async () => {
    const payload = { ...listing };
    // strip empty-string contact_email so pydantic's EmailStr doesn't choke
    if (!payload.contact_email) payload.contact_email = null;
    await apiClient.put("/me/teacher-listing", payload);
    toast.success(listing.listed ? t("Listing published") : t("Listing saved (not yet public)"));
  };

  const set = (k, v) => setListing((l) => ({ ...l, [k]: v }));

  return (
    <div className="space-y-4" data-testid="teacher-listing-editor">
      <div className="bento-card p-5 flex items-center gap-4">
        <Switch checked={!!listing.listed} onCheckedChange={(v) => set("listed", v)} data-testid="listing-listed-toggle" />
        <div className="flex-1">
          <div className="font-display font-bold">{t("Show my studio in \"Find a teacher\" search")}</div>
          <div className="text-sm" style={{ color: "var(--app-text-muted)" }}>
            {t("When turned on, solo learners in your city can discover you and reach out.")}
          </div>
        </div>
      </div>

      <div className="bento-card p-5 space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>{t("City")}</Label>
            <Input list="tl-cities" value={listing.city || ""} onChange={(e) => set("city", e.target.value)}
                   placeholder={t("e.g. Nashville")} data-testid="listing-city" />
            <datalist id="tl-cities">
              {cities.map((c) => <option key={c.id || c.city} value={c.city} />)}
            </datalist>
          </div>
          <div>
            <Label>{t("State")}</Label>
            <Input value={listing.state || ""} onChange={(e) => set("state", e.target.value)}
                   placeholder={t("e.g. TN")} data-testid="listing-state" />
          </div>
        </div>
        <div>
          <Label>{t("About your studio")}</Label>
          <Textarea
            rows={4}
            value={listing.bio || ""}
            onChange={(e) => set("bio", e.target.value)}
            placeholder={t("15 years teaching piano and voice. Studio in East Nashville, recitals twice a year.")}
            data-testid="listing-bio"
          />
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>{t("Rate — 30 min ($)")}</Label>
            <Input type="number" value={listing.hourly_rate_30min ?? ""} onChange={(e) => set("hourly_rate_30min", e.target.value === "" ? null : Number(e.target.value))}
                   data-testid="listing-rate-30" />
          </div>
          <div>
            <Label>{t("Rate — 60 min ($)")}</Label>
            <Input type="number" value={listing.hourly_rate_60min ?? ""} onChange={(e) => set("hourly_rate_60min", e.target.value === "" ? null : Number(e.target.value))}
                   data-testid="listing-rate-60" />
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          <label className="inline-flex items-center gap-2">
            <Switch checked={!!listing.teaches_at_studio} onCheckedChange={(v) => set("teaches_at_studio", v)} data-testid="listing-at-studio-toggle" />
            {t("Teaches in studio")}
          </label>
          <label className="inline-flex items-center gap-2">
            <Switch checked={!!listing.teaches_at_student} onCheckedChange={(v) => set("teaches_at_student", v)} data-testid="listing-at-student-toggle" />
            {t("Drives to student")}
          </label>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>{t("Contact email")}</Label>
            <Input type="email" value={listing.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)}
                   placeholder={t("you@example.com")} data-testid="listing-contact-email" />
          </div>
          <div>
            <Label>{t("Contact phone")}</Label>
            <Input value={listing.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)}
                   placeholder={t("(555) 123-4567")} data-testid="listing-contact-phone" />
          </div>
        </div>
        <Button onClick={save} className="btn-pill btn-primary h-10" data-testid="listing-save">
          {listing.listed ? t("Publish listing") : t("Save draft")}
        </Button>
      </div>
    </div>
  );
}

export default function ResourceHubPage() {
  const t = useT();
  return (
    <div className="space-y-6" data-testid="resource-hub">
      <div>
        <h1 className="font-display text-4xl font-bold flex items-center gap-3"><Layers className="w-8 h-8" /> {t("Resource Hub")}</h1>
        <p style={{ color: "var(--app-text-muted)" }}>{t("City pricing, your public teacher listing, and your custom student fields.")}</p>
      </div>
      <Tabs defaultValue="pricing">
        <TabsList>
          <TabsTrigger value="pricing" data-testid="tab-pricing"><MapPin className="w-4 h-4 mr-1" /> {t("City pricing")}</TabsTrigger>
          <TabsTrigger value="listing" data-testid="tab-listing"><GraduationCap className="w-4 h-4 mr-1" /> {t("Public listing")}</TabsTrigger>
          <TabsTrigger value="custom" data-testid="tab-custom">{t("Custom fields")}</TabsTrigger>
        </TabsList>
        <TabsContent value="pricing" className="mt-6"><PricingTable /></TabsContent>
        <TabsContent value="listing" className="mt-6"><TeacherListingEditor /></TabsContent>
        <TabsContent value="custom" className="mt-6"><CustomFields /></TabsContent>
      </Tabs>
    </div>
  );
}
